import {
  BooleanParam,
  StringParam,
  withDefault,
  useQueryParams,
  NumberParam,
} from 'use-query-params'
import { Icon } from '@iconify/react'

import Header from 'components/content-ideas/Header'
import TopicSuggestionsTable from 'container/Projects/LiveProjects/Project/IdeaLab/IdeaLabDrawer/TopicSuggestionsTable'
import KeywordSuggestionsTable from 'container/Projects/LiveProjects/Project/IdeaLab/IdeaLabDrawer/KeywordSuggestionsTable'
import IdeaLabDrawer from 'container/Projects/LiveProjects/Project/IdeaLab/IdeaLabDrawer'
import AppliedKeywordsFilters from 'container/Projects/LiveProjects/Project/IdeaLab/Keywords/Filter/AppliedFilters'
import AppliedTopicsFilters from 'container/Projects/LiveProjects/Project/IdeaLab/Topics/Filter/AppliedFilters'
import Themes from '../Themes'
import Switch from '../Switch'
import Categories from '../Categories'
import BreadCrumbs from '../BreadCrumbs'

import { useKeywordCategories } from 'container/SEOTools/hooks/useKeywordCategories'
import { useGetRelatedCategories } from 'utils/hooks/content-ideas/useGetRelatedCategories'

import { Domain } from '@pepper/utils/src/types/seo'

import s from './ContentIdeasDrawer.module.css'

type Props = {
  companyId: string
  domain: Domain
  projectId: string
  onClose?: () => void
}

const ContentIdeasDrawer = ({
  companyId,
  domain,
  onClose,
  projectId,
}: Props) => {
  const [queryParams, setQueryParams] = useQueryParams({
    content_ideas: withDefault(BooleanParam, false),
    category_id: withDefault(StringParam, undefined),
    step: withDefault(StringParam, undefined),
    theme_id: withDefault(StringParam, undefined),
    query: withDefault(StringParam, undefined),
    sug_page: withDefault(NumberParam, 1),
    sug_limit: withDefault(NumberParam, 15),
    'sug_volume:lte': withDefault(NumberParam, undefined),
    'sug_volume:gte': withDefault(NumberParam, undefined),
    'sug_difficulty:lte': withDefault(NumberParam, undefined),
    'sug_difficulty:gte': withDefault(NumberParam, undefined),
    sort: withDefault(StringParam, undefined),
    sug_positionGte: withDefault(NumberParam, undefined),
    sug_positionLte: withDefault(NumberParam, undefined),
    sug_exclude_position: withDefault(BooleanParam, false),
    sug_keyword: withDefault(StringParam, undefined),
    sug_topic: withDefault(StringParam, undefined),
  })

  const { data: categories, isLoading: isRecentCategoriesLoading } =
    useKeywordCategories({
      companyId,
      domainId: domain.id,
      enabled: !!companyId && !!domain.id && queryParams.content_ideas,
    })

  const {
    data: recommendedCategories,
    isLoading: isRecommendedCategoriesLoading,
  } = useGetRelatedCategories({
    companyId,
    domainId: domain.id,
    options: {
      enabled: !!domain.id,
    },
  })

  const isDomainRemoved = !!domain?.deleted_at

  const {
    content_ideas: isDrawerOpen,
    category_id: categoryId,
    step,
    theme_id: themeId,
    sug_exclude_position,
    sug_keyword,
  } = queryParams

  const activeCategoryName = categories?.data?.find(
    category => category.id === categoryId
  )?.category

  const handleClose = () => {
    setQueryParams({
      content_ideas: undefined,
      theme_id: undefined,
      step: undefined,
      category_id: undefined,
      sug_limit: undefined,
      sug_page: undefined,
      sug_exclude_position: undefined,
      sug_keyword: undefined,
      sug_positionGte: undefined,
      sug_positionLte: undefined,
      sug_topic: undefined,
      sort: undefined,
      'sug_difficulty:gte': undefined,
      'sug_difficulty:lte': undefined,
      'sug_volume:gte': undefined,
      'sug_volume:lte': undefined,
    })
    onClose?.()
  }

  const isFilterApplied = sug_exclude_position || !!sug_keyword

  const isTopicSuggestionsFilterApplied =
    isFilterApplied || !!queryParams.sug_topic

  const isKeywordSuggestionsFilterApplied =
    isFilterApplied ||
    !!queryParams['sug_difficulty:gte'] ||
    !!queryParams['sug_difficulty:lte'] ||
    !!queryParams['sug_volume:gte'] ||
    !!queryParams['sug_volume:lte']

  const drawerTitle =
    step && activeCategoryName ? (
      <BreadCrumbs
        categoryName={activeCategoryName}
        companyId={companyId}
        domainId={domain.id}
      />
    ) : (
      <div className={s.title}>
        <Icon
          icon="heroicons:sparkles-solid"
          width={18}
          height={18}
          className={s.icon}
        />
        Content Ideas
      </div>
    )

  return (
    <IdeaLabDrawer
      open={isDrawerOpen}
      onClose={handleClose}
      title={drawerTitle}
    >
      {(!step || step === 'themes') && (
        <Header companyId={companyId} domainId={domain.id} />
      )}
      {!step && categories && (
        <Categories
          domainId={domain.id}
          companyId={companyId}
          isLoading={
            isRecentCategoriesLoading || isRecommendedCategoriesLoading
          }
          recentCategories={categories.data}
          recommendedCategories={recommendedCategories?.map(
            category => category.category
          )}
        />
      )}

      {step === 'themes' && (
        <Themes
          domainId={domain.id}
          companyId={companyId}
          categoryId={categoryId}
          categoryName={activeCategoryName}
        />
      )}
      {(step === 'topic_suggestions' || step === 'keyword_suggestions') && (
        <Switch />
      )}

      {step === 'topic_suggestions' && !!themeId && (
        <>
          {isTopicSuggestionsFilterApplied && (
            <div className={s.filter}>
              <AppliedTopicsFilters isSuggestionsFilter={true} />
            </div>
          )}
          <TopicSuggestionsTable
            domainId={domain.id}
            isDomainRemoved={isDomainRemoved}
            companyId={companyId}
            projectId={projectId}
            domainRegion={domain.region_code}
            themeId={themeId}
          />
        </>
      )}
      {step === 'keyword_suggestions' && !!themeId && (
        <>
          {isKeywordSuggestionsFilterApplied && (
            <div className={s.filter}>
              <AppliedKeywordsFilters isSuggestionsFilter={true} />
            </div>
          )}
          <KeywordSuggestionsTable
            domainId={domain.id}
            isDomainRemoved={isDomainRemoved}
            companyId={companyId}
            projectId={projectId}
            themeId={themeId}
          />
        </>
      )}
    </IdeaLabDrawer>
  )
}

export default ContentIdeasDrawer
